import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QRScanner = ({ onScanSuccess, loading, error }) => {
  const [scanning, setScanning] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    let scanner = null;

    if (scanning && !manualEntry) {
      scanner = new Html5QrcodeScanner(
        'staff-qr-reader',
        {
          fps: 10,
          qrbox: { width: 280, height: 280 },
          aspectRatio: 1.0
        },
        false
      );

      scanner?.render(
        (decodedText) => {
          setScanning(false);
          scanner?.clear();
          onScanSuccess?.(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors during scanning
        }
      );
    }

    return () => {
      if (scanner) {
        scanner?.clear()?.catch(() => {});
      }
    };
  }, [scanning, manualEntry]);

  const startScanning = () => {
    setScanning(true);
    setManualEntry(false);
  };

  const stopScanning = () => {
    setScanning(false);
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualCode?.trim()) {
      onScanSuccess?.(manualCode?.trim());
    }
  };

  return (
    <div className="space-y-6">
      {!scanning && !manualEntry && (
        <>
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-72 h-72 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center border-2 border-dashed border-primary/30">
                <Icon name="QrCode" size={120} className="text-primary/40" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-2">Scan Your QR Code</h2>
            <p className="text-muted-foreground mb-6">
              Position your staff QR code in front of the camera
            </p>

            <div className="space-y-3">
              <Button 
                onClick={startScanning} 
                size="lg" 
                className="w-full max-w-md mx-auto"
                disabled={loading}
              >
                <Icon name="Camera" size={20} />
                <span>Start Camera Scan</span>
              </Button>

              <Button 
                onClick={() => setManualEntry(true)} 
                variant="outline"
                size="lg" 
                className="w-full max-w-md mx-auto"
                disabled={loading}
              >
                <Icon name="Keyboard" size={20} />
                <span>Enter Code Manually</span>
              </Button>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-error/10 rounded-lg border border-error/20 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="p-4 bg-warning/5 rounded-lg border border-warning/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Camera Permission Required</p>
                <p>Please allow camera access when prompted to scan your QR code.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {scanning && !manualEntry && (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold text-foreground mb-2">Scanning...</h2>
            <p className="text-muted-foreground">Position your QR code within the frame</p>
          </div>

          <div id="staff-qr-reader" className="mx-auto max-w-md rounded-lg overflow-hidden"></div>

          <Button 
            onClick={stopScanning} 
            variant="outline" 
            className="w-full max-w-md mx-auto"
          >
            <Icon name="X" size={20} />
            <span>Cancel Scanning</span>
          </Button>
        </div>
      )}

      {manualEntry && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Keyboard" size={40} className="text-primary" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-2">Manual Code Entry</h2>
            <p className="text-muted-foreground mb-6">
              Enter your staff QR code identifier
            </p>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e?.target?.value)}
                placeholder="Enter QR code identifier"
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <Button 
                type="submit"
                size="lg" 
                className="flex-1"
                disabled={loading || !manualCode?.trim()}
              >
                {loading ? (
                  <Icon name="Loader2" size={20} className="animate-spin" />
                ) : (
                  <Icon name="ArrowRight" size={20} />
                )}
                <span>Continue</span>
              </Button>

              <Button 
                type="button"
                onClick={() => {
                  setManualEntry(false);
                  setManualCode('');
                }} 
                variant="outline"
                size="lg"
                disabled={loading}
              >
                <Icon name="ArrowLeft" size={20} />
                <span>Back</span>
              </Button>
            </div>
          </form>

          {error && (
            <div className="p-4 bg-error/10 rounded-lg border border-error/20 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRScanner;